import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { TransactionItem } from '../models/TransactionItem';
import { TransactionEntity } from '../models/TransactionEntity';
import { TransactionFetchFilter } from '../models/TransactionFetchFilter';

/** Request body for POST /api/transactions/create/{user_id} */
export interface CreateTransactionData {
  transaction_record_id: number;
  transaction_date: string;
  transaction_action_id: number;
  transaction_object_id: number;
  transaction_indirect_object_id?: number | null;
  transaction_media_id: number;
  transaction_quantity: number;
  transaction_total_amount: number;
}

/** Response from POST /api/transactions/create/{user_id} */
export interface CreateTransactionApiResponse {
  message: string;
  transaction: {
    transaction_record_id: number;
    transaction_date: string;
    transaction_action_id: number;
    transaction_object_id: number;
    transaction_indirect_object_id: number | null;
    transaction_media_id: number;
    transaction_quantity: number;
    transaction_total_amount: number;
    created_by_user_id: string | number;
    id: number;
  };
}

/** Request body for PUT /api/transactions/{transaction_id}/{user_id} */
export interface UpdateTransactionData {
  transaction_record_id: number;
  transaction_date: string;
  transaction_action_id: number;
  transaction_object_id: number;
  transaction_indirect_object_id?: number | null;
  transaction_media_id: number;
  transaction_quantity: number;
  transaction_total_amount: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

/** Raw transaction entity as returned by the API (nested in transaction) */
export interface TransactionEntityApi {
  id?: number;
  transaction_entity_name?: string;
  transaction_entity_description?: string | null;
  transaction_entity_category?: string;
  transaction_entity_type?: string | null;
  transaction_entity_past_tense?: string | null;
}

/** Raw transaction item as returned by the API (transactions.data[]) */
export interface TransactionApiItem {
  id: number;
  transaction_record_id: number;
  transaction_date: string;
  transaction_action_id: number;
  transaction_object_id: number;
  transaction_indirect_object_id: number;
  transaction_media_id: number;
  transaction_quantity: number;
  transaction_total_amount: number;
  created_by_user_id: number;
  action?: TransactionEntityApi;
  object?: TransactionEntityApi;
  indirect_object?: TransactionEntityApi;
  media?: TransactionEntityApi;
}

/** Pagination wrapper as returned by the API */
export interface TransactionsPaginationApi {
  current_page: number;
  data: TransactionApiItem[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{ url: string | null; label: string; active: boolean }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface TransactionsApiResponse {
  message: string;
  transactions: TransactionsPaginationApi;
}

/** Maps API transaction entity to frontend TransactionEntity */
function mapApiEntityToEntity(api?: TransactionEntityApi): TransactionEntity | undefined {
  if (!api) return undefined;
  return new TransactionEntity({
    id: api.id,
    transaction_entity_name: api.transaction_entity_name,
    transaction_entity_description: api.transaction_entity_description ?? undefined,
    transaction_entity_category: api.transaction_entity_category,
    transaction_entity_type: api.transaction_entity_type ?? undefined,
    transaction_entity_past_tense: api.transaction_entity_past_tense ?? undefined,
  });
}

/** Maps a single API transaction to frontend TransactionItem */
export function mapApiTransactionToItem(api: TransactionApiItem): TransactionItem {
  return new TransactionItem({
    id: api.id,
    record_id: api.transaction_record_id,
    dateTime: api.transaction_date,
    action: mapApiEntityToEntity(api.action),
    direct_object: mapApiEntityToEntity(api.object),
    indirect_object: mapApiEntityToEntity(api.indirect_object),
    qty: api.transaction_quantity,
    paymentMedia: mapApiEntityToEntity(api.media),
        amount: api.transaction_total_amount,
    created_by_user_id: api.created_by_user_id,
  });
}

class TransactionsService {
  // Get transactions for a user with optional filters (POST method with body)
  async getTransactions(
    token: string,
    userId: number,
    filters?: TransactionFetchFilter
  ): Promise<ApiResponse<TransactionsApiResponse>> {
    try {
      const body: Record<string, unknown> = {};

      if (filters) {
        if (filters.record_id !== undefined) body.record_id = filters.record_id;
        if (filters.id !== undefined) body.id = filters.id;
        if (filters.start_date) body.start_date = filters.start_date;
        if (filters.end_date) body.end_date = filters.end_date;
        if (filters.transaction_action_id !== undefined) body.transaction_action_id = filters.transaction_action_id;
        if (filters.transaction_object_id !== undefined) body.transaction_object_id = filters.transaction_object_id;
        if (filters.transaction_indirect_object_id !== undefined) body.transaction_indirect_object_id = filters.transaction_indirect_object_id;
        if (filters.transaction_media_id !== undefined) body.transaction_media_id = filters.transaction_media_id;
        if (filters.quantity_min !== undefined) body.quantity_min = filters.quantity_min;
        if (filters.quantity_max !== undefined) body.quantity_max = filters.quantity_max;
        if (filters.amount_min !== undefined) body.amount_min = filters.amount_min;
        if (filters.amount_max !== undefined) body.amount_max = filters.amount_max;
        if (filters.created_by_user_id !== undefined) body.created_by_user_id = filters.created_by_user_id;
        if (filters.limit !== undefined) body.limit = filters.limit;
        if (filters.page !== undefined) body.page = filters.page;
      }

      const url = UrlBuilder.transactions.find(userId);
      const response = await apiClient.postWithToken<TransactionsApiResponse>(url, token, body);

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  // Create a new transaction with token
  async createTransaction(
    token: string,
    userId: number,
    data: CreateTransactionData
  ): Promise<ApiResponse<CreateTransactionApiResponse>> {
    try {
      const response = await apiClient.postWithToken<CreateTransactionApiResponse>(
        UrlBuilder.transactions.create(userId),
        token,
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  // Update a transaction with token. PUT /api/transactions/{transaction_id}/{user_id}
  async updateTransaction(
    token: string,
    transactionId: number,
    userId: number,
    data: UpdateTransactionData
  ): Promise<ApiResponse<CreateTransactionApiResponse['transaction']>> {
    try {
      const response = await apiClient.putWithToken<CreateTransactionApiResponse['transaction']>(
        UrlBuilder.transactions.edit(transactionId, userId),
        token,
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  async deleteTransaction(
    token: string,
    transactionId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await apiClient.deleteWithToken(
        UrlBuilder.transactions.delete(transactionId),
        token,
      );

      if (response.success) {
        return {
          success: true,
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }
}

export const transactionsService = new TransactionsService();